import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ConfirmDialog, toast } from '@wit-support/ui';
import { activateLaunch, pauseLaunch, retryLaunch, reverifyLaunch, rollbackLaunch, } from '../api.js';
import { ADS_KEY, describeAdsError, isUpstreamCode } from '../shared.js';
import { useAdsLauncherPerms } from '../perms.js';
import { formatStartInZone } from '../format.js';
import { LateActivateDialog, showActivationOutcome } from './late-activation.js';
import { useAdsErrorToast } from './journal-link.js';
import { spendTotalText } from './spend-summary.js';
import { isTakenBackPaused } from '../attention-texts.js';
export const LAUNCH_ACTION_LABEL = {
    retry: 'Повторить',
    activate: 'Включить',
    force: 'Включить всё равно',
    // v4 §8.2: старт прошёл, запуск ждёт включения — «сейчас» (диалог с суммой)
    activate_late: 'Включить сейчас',
    force_activate_late: 'Включить сейчас всё равно',
    pause: 'Пауза',
    reverify: 'Перепроверить',
    rollback: 'Откатить',
};
/**
 * Порядок кнопок: «Пауза» первой — у живого запуска это главное действие
 * («Ждёт старта», «Работает»); опасные — в конце.
 */
const ACTION_ORDER = [
    'pause',
    'activate_late',
    'activate',
    'force_activate_late',
    'force',
    'retry',
    'reverify',
    'rollback',
];
/** Имена действий auto-ads → наши (написание сервера может отличаться). */
const SERVER_ACTION = {
    retry: 'retry',
    activate: 'activate',
    force: 'force',
    force_activate: 'force',
    'force-activate': 'force',
    forceActivate: 'force',
    activate_late: 'activate_late',
    force_activate_late: 'force_activate_late',
    pause: 'pause',
    reverify: 'reverify',
    verify: 'reverify',
    're-verify': 'reverify',
    rollback: 'rollback',
};
function allowedByPerms(a, perms) {
    if (a === 'activate' || a === 'activate_late')
        return perms.canActivate;
    if (a === 'force' || a === 'force_activate_late')
        return perms.canForceActivate;
    return perms.canLaunch;
}
/**
 * Показ начался: после старта запуск кампанию не ведёт — действий над ней с его страницы нет,
 * кампанией управляют во вкладке «Кампании». Новый auto-ads говорит это сам (`deliveryStartedAt`,
 * `phase`); старый (поля нет) — статус `running`.
 */
export function deliveryStarted(l) {
    // новый auto-ads: решает `phase`; статус `running` — только когда `phase` не пришла (старый)
    if (l.phase != null)
        return l.phase === 'started';
    return ((l.deliveryStartedAt != null && l.deliveryStartedAt !== '') || l.displayStatus === 'running');
}
/** Объекты запуска включены и ещё не начали показ (кампания вот-вот начнёт). `running` сюда НЕ входит. */
const LIVE_STATUSES = new Set(['activating', 'waiting_start', 'pause_failed', 'needs_attention']);
/**
 * Какие действия доступны запуску сейчас. Есть `availableActions` от сервера
 * — только они (и только по правам). Нет (старый auto-ads) — по статусу:
 * «Включить» — только при чистой сверке; «Включить всё равно» — только
 * владелец и только при расхождениях; «Откатить» у включённого запуска — только
 * когда он на паузе: из-за подмены (`activation.status = paused_on_drift`) или
 * вручную (`paused`, `manualPause`). После начала показа (`deliveryStarted`) действий нет.
 */
export function availableActions(l, perms) {
    // сервер сам говорит, что показ начался (новый auto-ads): никаких действий над запуском
    // то же правило, что у `deliveryStarted`: `phase` решает, когда пришла; `deliveryStartedAt` — запасной
    const startedByServer = l.phase != null
        ? l.phase === 'started'
        : l.deliveryStartedAt != null && l.deliveryStartedAt !== '';
    if (startedByServer)
        return [];
    if (l.availableActions != null) {
        const fromServer = new Set(l.availableActions.map((a) => SERVER_ACTION[a]).filter((a) => a != null));
        // всё уже на паузе после отката «создавать включённым» — «Пауза» не нужна
        // (v5 N2; новый auto-ads её и не присылает, это — для старого)
        if (isTakenBackPaused(l))
            fromServer.delete('pause');
        return ACTION_ORDER.filter((a) => fromServer.has(a) && allowedByPerms(a, perms));
    }
    // старый auto-ads (списка действий нет): запущенному запуску действия не предлагаем
    if (deliveryStarted(l))
        return [];
    const out = new Set();
    const s = l.displayStatus;
    const created = l.created.campaignId != null;
    const dirty = l.verify?.hasErrors === true;
    const gone = s === 'rolling_back' || s === 'rolled_back';
    const live = l.state === 'active' || LIVE_STATUSES.has(s);
    // Всё на паузе — подменой или человеком: откат разрешён (сервер перечитает Meta).
    const pausedOnDrift = l.activation?.status === 'paused_on_drift' ||
        s === 'paused_on_drift' ||
        s === 'paused' ||
        l.manualPause != null;
    if (s === 'failed')
        out.add('retry');
    if (s === 'ready_paused' && !dirty)
        out.add('activate');
    if (s === 'blocked' || (s === 'ready_paused' && dirty))
        out.add('force');
    // `pause_failed` / `needs_attention` — кампания живая (пауза не прошла или
    // её не ставили): остановить может только человек.
    if (LIVE_STATUSES.has(s))
        out.add('pause');
    if (created && !gone)
        out.add('reverify');
    if (created && !gone && (!live || pausedOnDrift))
        out.add('rollback');
    return ACTION_ORDER.filter((a) => out.has(a) && allowedByPerms(a, perms));
}
/** Опасные действия: красная кнопка, пункт меню — «опасный». */
export const DANGER_ACTIONS = new Set([
    'force',
    'force_activate_late',
    'rollback',
]);
const CONFIRM = {
    force: {
        title: 'Включить при расхождениях?',
        text: 'Сверка нашла ошибки: Meta сохранила не то, что мы отправили. Включение запустит расход с этими настройками. Действие записывается в аудит.',
        danger: true,
    },
    rollback: {
        title: 'Откатить запуск?',
        text: 'Созданные кампания, группы и объявления будут удалены из Meta. Отменить откат нельзя.',
        danger: true,
    },
    activate: {
        title: 'Включить запуск?',
        text: 'Кампания включится и начнёт тратить бюджет со своего времени старта.',
        danger: false,
    },
};
/**
 * Отказы, после которых действовать надо через пачку: повтор/правка запуска
 * пачки (`batch-owned`), включение при отменённой пачке (`batch-cancelled`) или
 * прошедшем старте (`start-passed` — перепланировать через пачку).
 */
const BATCH_CODES = ['batch-owned', 'batch-cancelled', 'start-passed'];
/**
 * «Включить запуск?» до старта (§11.5): «…начнёт тратить около $80.00 в день
 * со своего времени старта 30.10 00:00» — сумма из `launch.spend` сервера;
 * неизвестна — прежний текст.
 */
export function activateConfirmText(launch) {
    const base = CONFIRM.activate.text;
    const spend = launch?.spend;
    if (!launch || spend == null || spend.usable === false)
        return base;
    const start = formatStartInZone({
        startTime: launch.startTime ?? null,
        startTimeLocal: launch.startTimeLocal ?? null,
        startTimeZone: launch.startTimeZone ?? null,
    });
    return `Кампания включится и начнёт тратить около ${spendTotalText(spend)} в день со своего времени старта${start ? ` ${start}` : ''}.`;
}
/** Отказ повтора, который вернула пачка (несёт `batchId`): `invalid-state`, `preview-stale`. */
const BATCH_IF_SCOPED_CODES = ['invalid-state', 'preview-stale'];
const META_OBJECT_RU = {
    campaign: 'кампания',
    adset: 'группа',
    ad: 'объявление',
    creative: 'креатив',
};
/**
 * 409 `rollback-forbidden-active`: что в Meta ещё включено (`details.active`)
 * — «Откат невозможен: включены кампания 1200…, группа 1200…. Сначала
 * поставьте запуск на паузу.» Без списка — общая фраза шлюза.
 */
export function rollbackForbiddenText(details) {
    const active = Array.isArray(details.active) ? details.active : [];
    const names = active.flatMap((a) => {
        if (a === null || typeof a !== 'object')
            return [];
        const { kind, metaId } = a;
        if (typeof kind !== 'string' || typeof metaId !== 'string')
            return [];
        return [`${META_OBJECT_RU[kind] ?? kind} ${metaId}`];
    });
    if (names.length === 0)
        return null;
    // согласование: «включена кампания …», «включено объявление …», «включены кампания …, группа …»
    const single = {
        campaign: 'включена',
        adset: 'включена',
        ad: 'включено',
        creative: 'включён',
    };
    const kind0 = active[0]?.kind;
    const verb = names.length === 1 && typeof kind0 === 'string' ? (single[kind0] ?? 'включены') : 'включены';
    return `Откат невозможен: в Meta ${verb} ${names.join(', ')}. Сначала поставьте запуск на паузу.`;
}
/**
 * Мутации действий над запуском + подтверждение для опасных. Возвращает
 * `run(action)` и элемент диалога, который вызывающий рендерит рядом.
 *
 * «Повторить» запуска пачки auto-ads передаёт пачке (перепланирование,
 * проверка опоздания, восстановление) — ответ `via: 'batch'`, тост говорит об
 * этом и ведёт на пачку (или на запуск, если мы уже на странице пачки).
 */
export function useLaunchActions(launchId, opts = {}) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const perms = useAdsLauncherPerms();
    const [pending, setPending] = useState(null);
    // «Включить сейчас?» (v4 §11.5): после отказа `reverify-required` — сразу «Перепроверить и включить»
    const [late, setLate] = useState(null);
    const errorToast = useAdsErrorToast();
    const openBatch = (batchId) => ({
        label: 'Открыть пачку',
        onClick: () => void navigate({ to: '/fb-launches/batches/$id', params: { id: batchId } }),
    });
    const openLaunch = () => ({
        label: 'Открыть запуск',
        onClick: () => void navigate({ to: '/fb-launches/launches/$id', params: { id: launchId } }),
    });
    const linkFor = (batchId) => opts.onBatchPage ? openLaunch() : batchId != null ? openBatch(batchId) : null;
    const mut = useMutation({
        // «Включить сейчас» сюда не попадает: флаг `acknowledgeLateStart` уходит только из
        // диалога с галочкой (`LateActivateDialog`), обойти подтверждение нельзя (ревью SB-5)
        mutationFn: async (a) => {
            switch (a) {
                case 'retry':
                    return retryLaunch(launchId);
                case 'activate':
                    return activateLaunch(launchId, false);
                case 'force':
                    return activateLaunch(launchId, true);
                case 'pause':
                    return pauseLaunch(launchId);
                case 'reverify':
                    return reverifyLaunch(launchId);
                case 'rollback':
                    return rollbackLaunch(launchId, {});
            }
        },
        onSuccess: (r, a) => {
            // повтор всегда передаётся пачке запуска
            if (a === 'retry') {
                const link = linkFor(r.batchId);
                const text = 'Повтор передан пачке: запуск перепланирован и поставлен в очередь';
                if (link)
                    toast.success(text, { action: link });
                else
                    toast.success(text);
            }
            else if (a === 'activate' || a === 'force') {
                // «включён» — только по статусу из ответа (Codex), а не «готово» по 2xx
                showActivationOutcome(r);
            }
            else if (a === 'pause') {
                const d = r;
                if (d.displayStatus === 'paused' || d.manualPause != null) {
                    toast.success('Запуск на паузе');
                }
                else {
                    toast.info('Пауза отправлена — статус обновится на карточке запуска');
                }
            }
            else {
                toast.success(a === 'rollback'
                    ? `${LAUNCH_ACTION_LABEL[a]}: поставлено в очередь`
                    : `${LAUNCH_ACTION_LABEL[a]}: готово`);
            }
            void qc.invalidateQueries({ queryKey: ADS_KEY });
        },
        onError: (err, a) => {
            const details = err instanceof ModuleRequestError ? err.details : {};
            const errBatchId = typeof details.batchId === 'string' ? details.batchId : null;
            const batchScoped = BATCH_CODES.some((code) => isUpstreamCode(err, code)) ||
                (errBatchId != null && BATCH_IF_SCOPED_CODES.some((code) => isUpstreamCode(err, code)));
            if (isUpstreamCode(err, 'start-passed') &&
                details.canActivateLate === true &&
                opts.launch != null) {
                // старт прошёл, пока смотрели экран: не на пачку, а «Включить сейчас?» (v4 §11.5);
                // при расхождениях сверки «сейчас всё равно» может только владелец
                if (details.needsForce === true && !perms.canForceActivate) {
                    errorToast(err, {
                        text: 'Время старта прошло, а сверка нашла расхождения — включить сейчас может только владелец.',
                    });
                }
                else {
                    setLate({ force: details.needsForce === true, reverify: false });
                }
            }
            else if (isUpstreamCode(err, 'reverify-required') && opts.launch != null) {
                setLate({ force: a === 'force', reverify: true });
            }
            else if (isUpstreamCode(err, 'verify-has-errors')) {
                errorToast(err, {
                    text: 'Сверка нашла расхождения — включение заблокировано. Смотрите таблицу сверки.',
                });
            }
            else if (isUpstreamCode(err, 'rollback-forbidden-active')) {
                errorToast(err, {
                    text: rollbackForbiddenText(details) ?? describeAdsError(err, 'write'),
                    action: { label: 'Поставить на паузу', onClick: () => run('pause') },
                });
            }
            else if (batchScoped) {
                const batchId = errBatchId ?? opts.batchId ?? null;
                const link = opts.onBatchPage ? null : batchId != null ? openBatch(batchId) : null;
                errorToast(err, { action: link });
            }
            else {
                errorToast(err);
            }
            void qc.invalidateQueries({ queryKey: ADS_KEY });
        },
    });
    const run = (a) => {
        if (a === 'activate_late' || a === 'force_activate_late') {
            setLate({ force: a === 'force_activate_late', reverify: false });
        }
        else if (CONFIRM[a])
            setPending(a);
        else
            mut.mutate(a);
    };
    // «Включить сейчас?» ответил `late-start-not-applicable` (запуск не опоздал) — обычное
    // «Включить запуск?» со своим подтверждением (§11.5)
    const onNotLate = () => {
        setLate(null);
        setPending('activate');
    };
    const c = pending ? CONFIRM[pending] : undefined;
    const lateLaunch = opts.launch ?? { id: launchId, lateStart: null, spend: null };
    const dialog = (_jsxs(_Fragment, { children: [_jsx(LateActivateDialog, { open: late != null, onOpenChange: (o) => (!o ? setLate(null) : null), launch: lateLaunch, force: late?.force ?? false, forceReverify: late?.reverify ?? false, onNotLate: onNotLate }), _jsx(ConfirmDialog, { open: pending != null, onOpenChange: (o) => (!o ? setPending(null) : null), title: c?.title ?? '', description: pending === 'activate' ? activateConfirmText(opts.launch) : (c?.text ?? ''), cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: pending ? LAUNCH_ACTION_LABEL[pending] : '', actionStyle: c?.danger ? 'danger' : 'primary', onConfirm: () => {
                    if (pending)
                        mut.mutate(pending);
                    setPending(null);
                } })] }));
    return { run, dialog, busy: mut.isPending };
}
//# sourceMappingURL=launch-actions.js.map